/**
 * Currency / locale-number helpers for the CURRENCY additional-field type.
 *
 * Design notes (why this file exists):
 *  - CURRENCY fields store a *canonical* numeric value (dot decimal, no
 *    grouping, e.g. "3519.5") — exactly like a DOUBLE — so the backend's
 *    `Double.parseDouble` and the Excel/CSV exporters get a real number they
 *    can do math on. The currency is fixed *per field* on the field
 *    definition (`Field.currencyCode`), not per value.
 *  - The user TYPES and SEES the value in their own locale. A German admin
 *    typing "3.519,50" means three-thousand-five-hundred-nineteen point five,
 *    NOT 3.519 — so we must parse with the user's locale separators, not
 *    JavaScript's dot-only `parseFloat`. That ambiguity was the root cause of
 *    the "Excel doesn't recognise my number" complaint.
 */

/**
 * ISO 4217 currency codes. Prefer the runtime list (`Intl.supportedValuesOf`,
 * ES2022 — available in all evergreen browsers we target) so the dropdown
 * always matches what `Intl.NumberFormat` can actually format. Falls back to a
 * curated common set on the rare engine without it.
 */
export function getIso4217Codes(): string[] {
  const intl = Intl as unknown as {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intl.supportedValuesOf === "function") {
    try {
      const codes = intl.supportedValuesOf("currency");
      if (Array.isArray(codes) && codes.length > 0) return codes;
    } catch {
      /* fall through to curated set */
    }
  }
  return COMMON_CURRENCY_CODES;
}

/** Small curated fallback (engines without Intl.supportedValuesOf). */
export const COMMON_CURRENCY_CODES = [
  "EUR", "USD", "GBP", "CHF", "JPY", "CAD", "AUD", "NZD", "SEK", "NOK",
  "DKK", "PLN", "CZK", "HUF", "RON", "BGN", "TRY", "RUB", "CNY", "HKD",
  "SGD", "INR", "BRL", "MXN", "ZAR", "AED", "SAR", "ILS", "KRW", "THB",
];

/** Default currency offered when an admin first picks the CURRENCY type. */
export const DEFAULT_CURRENCY_CODE = "EUR";

/** True when `code` is a syntactically valid 3-letter ISO 4217 code. */
export function isValidCurrencyCode(code: string | null | undefined): boolean {
  return typeof code === "string" && /^[A-Z]{3}$/.test(code);
}

/**
 * The currency symbol for `code` in `locale` (e.g. "€", "$", "CHF"). Used for
 * dropdown labels and the small symbol hint next to inputs. Returns the raw
 * code if Intl can't resolve a narrow symbol.
 */
const symbolCache = new Map<string, string>();
export function getCurrencySymbol(code: string, locale: string): string {
  const cacheKey = `${locale}|${code}`;
  const hit = symbolCache.get(cacheKey);
  if (hit !== undefined) return hit;
  let symbol = code;
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    symbol = parts.find((p) => p.type === "currency")?.value ?? code;
  } catch {
    symbol = code;
  }
  symbolCache.set(cacheKey, symbol);
  return symbol;
}

/**
 * Human label for a currency option: "EUR — € (Euro)". The currency *name*
 * comes from Intl's display-names in the user's language so the dropdown reads
 * naturally per locale.
 */
const labelCache = new Map<string, string>();
export function currencyOptionLabel(code: string, locale: string): string {
  const cacheKey = `${locale}|${code}`;
  const hit = labelCache.get(cacheKey);
  if (hit !== undefined) return hit;
  const symbol = getCurrencySymbol(code, locale);
  let name = "";
  try {
    name =
      new Intl.DisplayNames([locale], { type: "currency" }).of(code) ?? "";
  } catch {
    /* DisplayNames unsupported → code only */
  }
  const symbolPart = symbol && symbol !== code ? ` ${symbol}` : "";
  const namePart = name ? ` (${name})` : "";
  const label = `${code}${symbolPart}${namePart}`;
  labelCache.set(cacheKey, label);
  return label;
}

/**
 * Discover the grouping + decimal separators `locale` uses for plain decimal
 * numbers. Memoised per locale — `Intl.NumberFormat` construction is not free.
 */
const separatorCache = new Map<string, { group: string; decimal: string }>();
export function localeSeparators(locale: string): {
  group: string;
  decimal: string;
} {
  const cached = separatorCache.get(locale);
  if (cached) return cached;
  let group = ",";
  let decimal = ".";
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    group = parts.find((p) => p.type === "group")?.value ?? group;
    decimal = parts.find((p) => p.type === "decimal")?.value ?? decimal;
  } catch {
    /* keep en-US defaults */
  }
  const sep = { group, decimal };
  separatorCache.set(locale, sep);
  return sep;
}

/**
 * Parse a user-typed amount in `locale` into a canonical JS number.
 *
 * Strips currency symbols and whitespace, removes the locale's grouping
 * separator (so German "3.519" → 3519, not 3.519) and normalises the locale's
 * decimal separator to a dot. Returns `null` when the input has no digits or
 * isn't a finite number — callers treat null as "leave empty / keep previous".
 *
 * Robustness: if the string contains BOTH the group and decimal separators we
 * trust the locale mapping. If it contains only ONE kind of separator we look
 * at how it's used — a lone separator followed by exactly 3 digits and no other
 * separator is treated as grouping ("3.519" / "3,519" → 3519), otherwise as a
 * decimal point. This matches what a person filling in a money column means.
 */
export function parseLocaleNumber(
  input: string,
  locale: string,
): number | null {
  if (input == null) return null;
  let s = String(input).trim();
  if (s === "") return null;

  const { group, decimal } = localeSeparators(locale);

  // Keep sign, digits and any separator chars; drop currency symbols, spaces
  // (incl. non-breaking / narrow no-break used by some locales) and letters.
  s = s.replace(/[^\d.,  '\s+-]/g, "");
  s = s.replace(/[\s  ']/g, "");
  if (s === "" || s === "-" || s === "+") return null;

  const hasGroup = group && s.includes(group);
  const hasDecimal = s.includes(decimal);

  if (hasGroup && hasDecimal) {
    // Unambiguous: strip every group sep, normalise the decimal sep.
    s = s.split(group).join("");
    s = s.replace(decimal, ".");
  } else if (hasDecimal && !hasGroup) {
    // Only the locale DECIMAL separator is present → it is unambiguously the
    // decimal point. The decimal separator is NEVER a thousands grouping, so do
    // NOT apply the 3-digit heuristic here (that would turn de "3,519" into 3519
    // or en "1.999" into 1999). de "3.519" does NOT reach this branch — "." is
    // de's GROUP separator, so it hits the group-only branch below.
    // Multiple decimal chars (e.g. "1.2.3") collapse to "1.2.3" → Number = NaN
    // → null (correctly rejected as malformed).
    s = s.split(decimal).join(".");
  } else if (hasGroup && !hasDecimal) {
    // Only the GROUP separator is present (e.g. de "3.519"). The 3-digit
    // heuristic disambiguates a real grouping ("3.519" → 3519) from a
    // mistyped decimal ("3.5" → 3.5, where "5" isn't a full thousand-group).
    s = disambiguateSingle(s, group);
  }
  // else: no separators at all → already canonical.

  // Any leftover stray separators (defensive) become nothing.
  s = s.replace(/[^\d.+-]/g, "");

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Treat a lone separator as grouping when it splits the number into clean
 *  thousand-groups, else as a decimal point. */
function disambiguateSingle(s: string, sep: string): string {
  const occurrences = s.split(sep).length - 1;
  const escaped = sep.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (occurrences === 1) {
    const after = s.split(sep)[1] ?? "";
    // "1.234" / "1,234" — exactly three trailing digits → thousands grouping.
    if (/^\d{3}$/.test(after)) return s.replace(new RegExp(escaped), "");
    // otherwise it's a decimal point
    return s.replace(new RegExp(escaped), ".");
  }
  // Multiple identical separators can only be grouping ("1.234.567").
  return s.split(sep).join("");
}

/**
 * Format a canonical numeric value as currency in `locale` for DISPLAY.
 * Falls back to a grouped decimal if the currency code is missing/invalid so a
 * mis-configured field still shows a readable, separated number.
 */
export function formatCurrencyValue(
  value: number,
  currencyCode: string | null | undefined,
  locale: string,
): string {
  if (!Number.isFinite(value)) return "";
  if (isValidCurrencyCode(currencyCode)) {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode as string,
        currencyDisplay: "narrowSymbol",
      }).format(value);
    } catch {
      /* invalid code at runtime → grouped decimal */
    }
  }
  return formatGroupedDecimal(value, locale);
}

/**
 * Render a canonical number as an EDITABLE locale string for a text input:
 * the locale's decimal separator, NO grouping (grouping chars are noise while
 * typing). e.g. 12.345 + de → "12,345"; 1234.5 + de → "1234,5". This is what a
 * currency cell pre-fills so a re-parse via {@link parseLocaleNumber} is a
 * faithful round-trip (the canonical "12.345" would otherwise be misread as
 * 12345 by a German user, since "." is de's grouping separator).
 */
export function toEditableLocaleString(value: number, locale: string): string {
  if (!Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat(locale, {
      useGrouping: false,
      maximumFractionDigits: 20,
    }).format(value);
  } catch {
    return String(value);
  }
}

/**
 * Max fraction digits + rounding for DISPLAY of a grouped decimal. Pinned so
 * the four renderers stay byte-identical: FE (here), consumer (Intl), backend
 * (java.text HALF_UP), check-in. 10 caps IEEE-754 float tails (0.1+0.2 →
 * "0,3", not "0,30000000000000004") while keeping any realistic decimal.
 * KEEP IN SYNC: main-consumer humanize.formatNumber, main-backend
 * FieldValueFormatter.formatNumber, checkin number formatters.
 */
export const NUMBER_DISPLAY_MAX_FRACTION_DIGITS = 10;

/**
 * Minimum fraction digits for DOUBLE display — a plain decimal always shows at
 * least "2 Nachkommastellen" ("3,5" → "3,50", "3" → "3,00") while keeping any
 * extra precision ("3,527" stays "3,527"). Currency uses its own minor units
 * (via Intl currency style) and is unaffected.
 * KEEP IN SYNC across the same four renderers as the max above.
 */
export const NUMBER_DISPLAY_MIN_FRACTION_DIGITS = 2;

/** Locale-grouped plain decimal (the "give DOUBLE a thousands separator" fix). */
export function formatGroupedDecimal(value: number, locale: string): string {
  if (!Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: NUMBER_DISPLAY_MIN_FRACTION_DIGITS,
      maximumFractionDigits: NUMBER_DISPLAY_MAX_FRACTION_DIGITS,
      // halfExpand (round half away from zero) = Java RoundingMode.HALF_UP, so
      // the capped last digit matches across the JS/Java renderers.
      roundingMode: "halfExpand",
    } as Intl.NumberFormatOptions).format(value);
  } catch {
    return String(value);
  }
}

/**
 * Render a canonical number as a GROUPED editable string with the right minimum
 * fraction digits — used on blur so a currency/decimal input settles to a tidy
 * "3.519,00 €"-style form. Grouping is on (banking feel), the decimal separator
 * is the locale's.
 *  - CURRENCY (currencyCode set): pad to that currency's minor units (EUR/USD 2,
 *    JPY 0, BHD 3) as the MINIMUM so the editable draft matches the displayed
 *    currency value for typical amounts.
 *  - DOUBLE (no currencyCode): min 2.
 * The MAX is always {@link NUMBER_DISPLAY_MAX_FRACTION_DIGITS} (never the
 * currency's minor units) so a stored value with more precision than the minor
 * units (e.g. an imported "3519.567" EUR) is NOT silently rounded when the cell
 * is merely focused and blurred — the no-rewrite guard in the caller stays true.
 * Distinct from {@link toEditableLocaleString} (no grouping, no min) which was
 * the old draft form.
 */
export function padFractionOnBlur(
  value: number,
  locale: string,
  opts?: { currencyCode?: string | null },
): string {
  if (!Number.isFinite(value)) return "";
  let minFrac = NUMBER_DISPLAY_MIN_FRACTION_DIGITS;
  const maxFrac = NUMBER_DISPLAY_MAX_FRACTION_DIGITS;
  const currencyCode = opts?.currencyCode;
  if (isValidCurrencyCode(currencyCode)) {
    try {
      const ro = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode as string,
      }).resolvedOptions();
      // Minor units as the MINIMUM only; keep extra precision (capped at max).
      minFrac = Math.min(ro.minimumFractionDigits ?? minFrac, maxFrac);
    } catch {
      /* invalid at runtime → fall back to double min */
    }
  }
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: minFrac,
      maximumFractionDigits: maxFrac,
      useGrouping: true,
      roundingMode: "halfExpand",
    } as Intl.NumberFormatOptions).format(value);
  } catch {
    return String(value);
  }
}
